/** GPU posterize — fragment-shader port of
 *  `lib/filters/posterize.ts`.
 *
 *  Per-channel quantization to N evenly-spaced output values. The
 *  JS path pre-builds a 256-entry LUT; on the GPU the math is cheap
 *  enough to do directly per pixel (a floor + multiply), so no LUT
 *  upload needed.
 *
 *  Identity guard at levels=32 mirrors the JS path's identity
 *  short-circuit — at that bucket size the visual change is below
 *  perceivable, and copying the source is faster than running the
 *  shader. */

import { applyIdentityCopy } from '../identity-copy';
import type { PosterizeValue } from '../../filters/posterize';
import { applyShader } from '../runtime';

const POSTERIZE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_levels;     // 2..32, integer-valued at the float
uniform float u_step;       // 255.0 / (levels - 1), pre-computed CPU-side
out vec4 outColor;

float quantize(float v255) {
  // JS: bucket = floor((v * levels) / 256); out = round(bucket * step).
  // The /256 (not /255) is the JS path's actual divisor; we match it
  // exactly so the bucket boundaries land on the same input values.
  float bucket = floor((v255 * u_levels) / 256.0);
  return floor(bucket * u_step + 0.5);
}

void main() {
  vec4 c = texture(u_source, v_uv);
  vec3 v255 = c.rgb * 255.0;
  v255.r = clamp(quantize(v255.r), 0.0, 255.0);
  v255.g = clamp(quantize(v255.g), 0.0, 255.0);
  v255.b = clamp(quantize(v255.b), 0.0, 255.0);
  outColor = vec4(v255 / 255.0, c.a);
}
`;

export function applyPosterizeOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: PosterizeValue,
): boolean {
  const levels = Math.max(2, Math.min(32, Math.floor(value.levels)));
  if (levels === 32) {
    // Identity path: JS copies the source unchanged at this level.
    return applyIdentityCopy(source, target);
  }
  const step = 255 / (levels - 1);
  return applyShader({
    source,
    target,
    fragShaderSrc: POSTERIZE_FRAG,
    uniforms: {
      u_levels: { type: 'float', value: levels },
      u_step: { type: 'float', value: step },
    },
  });
}
